<template>
  <div class="landingWrapper">
    <section ref="heroRef">
      <HeroSection class="hero-section" />
    </section>
    <div class="landing-content layout-container">
      <ValueSection />
      <HowItWorksSection />
      <TeaserSection />
      <QuestionSection />
      <MarketAtlasSection />
      <HomeOwnerWorldSection />
      <LocationCarousel />
      <ArticleSection v-if="isArticleVisible" />
    </div>
    <StickyCTA v-if="stickyCTA.showSticky.value" />
  </div>
</template>

<script setup lang="ts">
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { useHead } from '@unhead/vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ArticleSection from '@/components/common/ArticleSection/ArticleSection.vue';
import HeroSection from '@/components/common/HeroSection/HeroSection.vue';
import HowItWorksSection from '@/components/common/HowItWorksSection/HowItWorksSection.vue';
import LocationCarousel from '@/components/common/LocationCarousel/LocationCarousel.vue';
import MarketAtlasSection from '@/components/common/MarketAtlasSection/MarketAtlasSection.vue';
import QuestionSection from '@/components/common/QuestionSection/QuestionSection.vue';
import HomeOwnerWorldSection from '@/components/landing-page/HomeOwnerWorldSection/HomeOwnerWorldSection.vue';
import StickyCTA from '@/components/landing-page/StickyCTA/StickyCTA.vue';
import TeaserSection from '@/components/landing-page/TeaserSection/TeaserSection.vue';
import ValueSection from '@/components/landing-page/ValueSection/ValueSection.vue';
import { useRel } from '@/composables/useRel';
import { useStickyOnIntersect } from '@/composables/useStickyOnIntersect';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';

const heroRef = ref<HTMLElement | null>(null);
const stickyCTA = useStickyOnIntersect(heroRef);

// Composables
const { t, locale } = useI18n();
const {
  getLandingPageCanonicalLink,
  getLandingPageAlternateLinks,
  getLandingPageDefaultAlternateLink,
} = useRel();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

// Methods extracted from mixins
const getPageTitle = (): string => {
  return t('metaInfo.title') as string;
};

const getPageDescription = (): string => {
  return t('metaInfo.description') as string;
};

const isArticleVisible = computed(() => {
  return locale.value !== HgSupportedLanguage.EN;
});

// Head metadata
useHead(() => {
  const metaTags = [{ name: 'description', content: getPageDescription() }];
  const metaLinks = [];

  return {
    title: getPageTitle(),
    htmlAttrs: {
      lang: locale.value,
    },
    meta: metaTags,
    link: [
      ...metaLinks,
      getLandingPageCanonicalLink(),
      ...getLandingPageAlternateLinks(),
      getLandingPageDefaultAlternateLink(),
    ],
  };
});

// Lifecycle
onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'landingpage',
    locale: locale.value,
    stepInputs: '',
  });
});
</script>

<style lang="scss" scoped>
@use '@/assets/variables' as *;

.landing-content {
  margin-top: $space-350;

  > div,
  > section {
    margin-bottom: $space-500;
  }
}

.landingWrapper {
  position: relative;
  overflow: visible;
}

.hero-section {
  margin-block: $space-200;
}
</style>
